import { lazy } from 'react'
import type { RouteObject } from 'react-router-dom'
import { OnboardingRouteWrapper } from '@/components/layout/public/OnboardingRouteWrapper'

// Lazy load public pages
const LandingPage = lazy(() => import('@/pages/public/LandingPage'))
const FeaturesPage = lazy(() => import('@/pages/public/FeaturesPage'))
const ContactPage = lazy(() => import('@/pages/public/ContactPage'))
const PricingPage = lazy(() => import('@/pages/public/PricingPage'))
const LogoutPage = lazy(() => import('@/pages/public/LogoutPage'))
const TermsPage = lazy(() => import('@/pages/public/legal/TermsPage'))
const PrivacyPage = lazy(() => import('@/pages/public/legal/PrivacyPage'))
const CancellationPage = lazy(() => import('@/pages/public/legal/CancellationPage'))
const NotFoundPage = lazy(() => import('@/pages/public/NotFoundPage'))

// Lazy load public onboarding pages
const VerifyBranchUserAccountPage = lazy(() =>
  import('@/pages/public/onboarding/VerifyBranchUserAccountPage').then(m => ({
    default: m.VerifyBranchUserAccountPage,
  }))
)
const AcceptBranchInvitationPage = lazy(() =>
  import('@/pages/public/onboarding/AcceptBranchInvitationPage').then(m => ({
    default: m.AcceptBranchInvitationPage,
  }))
)
const VerifyPlatformUserAccountPage = lazy(() =>
  import('@/pages/public/onboarding/VerifyPlatformUserAccountPage').then(m => ({
    default: m.VerifyPlatformUserAccountPage,
  }))
)
const AcceptPlatformInvitationPage = lazy(() =>
  import('@/pages/public/onboarding/AcceptPlatformInvitationPage').then(m => ({
    default: m.AcceptPlatformInvitationPage,
  }))
)
const OnboardingDashboardPage = lazy(() =>
  import('@/pages/public/onboarding/OnboardingDashboardPage').then(m => ({
    default: m.OnboardingDashboardPage,
  }))
)
const OnboardingStatusPage = lazy(() =>
  import('@/pages/public/onboarding/OnboardingStatusPage').then(m => ({
    default: m.OnboardingStatusPage,
  }))
)
const CompleteProfilePage = lazy(() =>
  import('@/pages/public/onboarding/CompleteProfilePage').then(m => ({
    default: m.CompleteProfilePage,
  }))
)
const OnboardingWizardPage = lazy(() =>
  import('@/pages/public/onboarding/OnboardingWizardPage').then(m => ({
    default: m.OnboardingWizardPage,
  }))
)

export const publicRoutesWithoutNotFound: RouteObject[] = [
  {
    path: '/',
    element: <LandingPage />,
  },
  {
    path: '/features',
    element: <FeaturesPage />,
  },
  {
    path: '/contact',
    element: <ContactPage />,
  },
  {
    path: '/pricing',
    element: <PricingPage />,
  },
  {
    path: '/logout',
    element: <LogoutPage />,
  },
  {
    path: '/onboarding',
    element: <OnboardingWizardPage />,
  },
  {
    path: '/terms-and-conditions',
    element: <TermsPage />,
  },
  {
    path: '/privacy-policy',
    element: <PrivacyPage />,
  },
  {
    path: '/cancellation-and-refund',
    element: <CancellationPage />,
  },
  // Public Onboarding Routes - Platform user routes (no slug, must come before branch routes)
  {
    path: '/verifyplatformuseraccount',
    element: <VerifyPlatformUserAccountPage />,
  },
  {
    path: '/accept-platform-invitation',
    element: <AcceptPlatformInvitationPage />,
  },
  // Public Onboarding Routes - Single slug routes (more specific, must come first)
  {
    path: '/:branchSlug/verifybranchuseraccount',
    element: <VerifyBranchUserAccountPage />,
  },
  {
    path: '/:branchSlug/accept-invitation',
    element: <AcceptBranchInvitationPage />,
  },
  {
    path: '/:branchSlug/onboarding/dashboard',
    element: (
      <OnboardingRouteWrapper>
        <OnboardingDashboardPage />
      </OnboardingRouteWrapper>
    ),
  },
  {
    path: '/:branchSlug/onboarding/status',
    element: (
      <OnboardingRouteWrapper>
        <OnboardingStatusPage />
      </OnboardingRouteWrapper>
    ),
  },
  {
    path: '/:branchSlug/onboarding/complete-profile',
    element: (
      <OnboardingRouteWrapper>
        <CompleteProfilePage />
      </OnboardingRouteWrapper>
    ),
  },
  // Public Onboarding Routes - Double slug routes (org + branch)
  {
    path: '/:organizationSlug/:branchSlug/verifybranchuseraccount',
    element: <VerifyBranchUserAccountPage />,
  },
  {
    path: '/:organizationSlug/:branchSlug/accept-invitation',
    element: <AcceptBranchInvitationPage />,
  },
  {
    path: '/:organizationSlug/:branchSlug/onboarding/dashboard',
    element: (
      <OnboardingRouteWrapper>
        <OnboardingDashboardPage />
      </OnboardingRouteWrapper>
    ),
  },
  {
    path: '/:organizationSlug/:branchSlug/onboarding/status',
    element: (
      <OnboardingRouteWrapper>
        <OnboardingStatusPage />
      </OnboardingRouteWrapper>
    ),
  },
  {
    path: '/:organizationSlug/:branchSlug/onboarding/complete-profile',
    element: (
      <OnboardingRouteWrapper>
        <CompleteProfilePage />
      </OnboardingRouteWrapper>
    ),
  },
]

/** Append after deferred app routes so /platform, /org, etc. resolve before the public catch-all. */
export const publicNotFoundRoute: RouteObject = {
  path: '*',
  element: <NotFoundPage />,
}

export const publicRoutes: RouteObject[] = [...publicRoutesWithoutNotFound, publicNotFoundRoute]

